import { LocationFile } from '@/api/documents'
import { Typography } from '@/components'
import { ddMMyyyy } from '@/utils'
import {
  Badge,
  ColumnDef,
  createColumnHelper,
  SortableHeader,
} from '@surein/ui'
import DownloadCell from './DownloadCell'
import { getInsuranceCategoryNameKey } from '@/utils/insuranceCategory'
import InsuranceCell from './InsuranceCell'
import PreviewCell from './PreviewCell'
import { TableCell } from './styles'

const columnHelper = createColumnHelper<LocationFile>()

const createColumns = (
  t: (key: string, options?: Record<string, boolean>) => string,
  companyId: string,
): ColumnDef<LocationFile, unknown>[] =>
  [
    columnHelper.accessor('displayName', {
      header: ({ column }) => (
        <SortableHeader column={column}>
          <TableCell margin="-8px 0 -8px -8px">
            {t('documentsPage.table.displayName')}
          </TableCell>
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <PreviewCell
          companyId={companyId}
          fileId={row.original.id}
          displayName={row.original.displayName}
          servedAt={row.original.servedAt}
        />
      ),
      sortingFn: (rowA, rowB) => {
        return rowA.original.displayName.localeCompare(
          rowB.original.displayName,
        )
      },
    }),
    columnHelper.accessor('category', {
      header: ({ column }) => (
        <SortableHeader column={column}>
          <TableCell margin="-8px 0 -8px -8px">
            {t('documentsPage.table.category')}
          </TableCell>
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <TableCell>
          <Typography ellipsis variant="p2Body">
            <Badge>
              {t(`documentsPage.table.categories.${row.original.category}`)}
            </Badge>
          </Typography>
        </TableCell>
      ),
      sortingFn: (rowA, rowB) => {
        return t(
          `documentsPage.table.categories.${rowA.original.category}`,
        ).localeCompare(
          t(`documentsPage.table.categories.${rowB.original.category}`),
        )
      },
      size: 200,
    }),
    columnHelper.accessor('insurance', {
      header: ({ column }) => (
        <SortableHeader column={column}>
          <TableCell margin="-8px 0 -8px -8px">
            {t('documentsPage.table.insurance')}
          </TableCell>
        </SortableHeader>
      ),
      cell: ({ row }) => {
        const {
          categoryId,
          insuranceId,
          locationId,
          carrier: { logoUrl },
        } = row.original.insurance

        return (
          <InsuranceCell
            categoryId={categoryId}
            insuranceId={insuranceId}
            locationId={locationId}
            logoUrl={logoUrl}
          />
        )
      },
      sortingFn: (rowA, rowB) => {
        const categoryA = t(
          getInsuranceCategoryNameKey(rowA.original.insurance.categoryId),
        )
        const categoryB = t(
          getInsuranceCategoryNameKey(rowB.original.insurance.categoryId),
        )

        return categoryA.localeCompare(categoryB)
      },
    }),
    columnHelper.accessor('policyNumber', {
      header: ({ column }) => (
        <SortableHeader column={column}>
          <TableCell margin="-8px 0 -8px -8px">
            {t('documentsPage.table.policyNumber')}
          </TableCell>
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <TableCell>
          <Typography ellipsis variant="p2Body">
            {row.original.policyNumber}
          </Typography>
        </TableCell>
      ),
      sortingFn: (rowA, rowB) => {
        return (rowA.original.policyNumber || '').localeCompare(
          rowB.original.policyNumber || '',
        )
      },
      size: 220,
    }),
    columnHelper.accessor('createdAt', {
      header: ({ column }) => (
        <SortableHeader column={column}>
          <TableCell margin="-8px 0 -8px -8px">
            {t('documentsPage.table.createdAt')}
          </TableCell>
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <TableCell>
          <Typography ellipsis variant="p2Body">
            {ddMMyyyy(Date.parse(row.original.createdAt))}
          </Typography>
        </TableCell>
      ),
      size: 120,
    }),
    columnHelper.accessor('id', {
      header: () => t('documentsPage.table.download'),
      cell: ({ row }) => (
        <DownloadCell companyId={companyId} fileId={row.original.id} />
      ),
    }),
  ] as Array<ColumnDef<LocationFile, unknown>>

export default createColumns
